import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Settings,
  Palette,
  Shield,
  CreditCard,
  HelpCircle,
  Lock,
  Sparkles,
  Loader2,
  Save,
  RotateCcw,
  CheckCircle2,
} from 'lucide-react';
import {
  loadSettings,
  persistSettings,
  applyThemeToDocument,
  DEFAULT_SETTINGS,
} from '../utils/settingsStorage';
import { confirmToast } from '../utils/notify';
import {
  setupTwoFactor,
  enableTwoFactor,
  disableTwoFactor,
  selectBillingPlan,
} from '../utils/api';

const TAB_ICONS = {
  general: Settings,
  appearance: Palette,
  security: Shield,
  billing: CreditCard,
  help: HelpCircle,
  privacy: Lock,
};

function settingsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

export default function SettingsModal({
  open,
  onClose,
  userEmail,
  account,
  onAccountRefresh,
  onSettingsChange,
  initialTab = 'general',
}) {
  const [tab, setTab] = useState(initialTab);
  const [pristine, setPristine] = useState(() => loadSettings());
  const [local, setLocal] = useState(() => loadSettings());
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const savedTimerRef = useRef(null);
  const [twoSetup, setTwoSetup] = useState(null);
  const [twoCode, setTwoCode] = useState('');
  const [twoDisableCode, setTwoDisableCode] = useState('');
  const [twoErr, setTwoErr] = useState('');
  const [twoBusy, setTwoBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(null);

  useEffect(() => {
    if (open) {
      const s = loadSettings();
      setPristine(s);
      setLocal(s);
      setTab(initialTab);
      setSaveState('idle');
    }
  }, [open, initialTab]);

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const dirty = useMemo(() => !settingsEqual(local, pristine), [local, pristine]);

  if (!open) return null;

  const patchLocal = (partial) => {
    setLocal((prev) => {
      const next = { ...prev, ...partial };
      if (partial.theme !== undefined || partial.reducedMotion !== undefined) {
        applyThemeToDocument(next);
      }
      return next;
    });
    if (saveState === 'saved') setSaveState('idle');
  };

  const handleSave = () => {
    if (!dirty) return;
    setSaveState('saving');
    persistSettings(local);
    applyThemeToDocument(local);
    setPristine(local);
    onSettingsChange?.(local);
    setSaveState('saved');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaveState('idle'), 1800);
  };

  const handleDiscard = () => {
    setLocal(pristine);
    applyThemeToDocument(pristine);
    setSaveState('idle');
  };

  const handleResetDefaults = async () => {
    const ok = await confirmToast({
      title: 'Reset all settings to defaults?',
      description: 'Your 2FA and billing settings are not affected.',
      confirmLabel: 'Reset',
      danger: true,
    });
    if (!ok) return;
    setLocal({ ...DEFAULT_SETTINGS });
    applyThemeToDocument(DEFAULT_SETTINGS);
    setSaveState('idle');
  };

  const handleClose = async () => {
    if (dirty) {
      const ok = await confirmToast({
        title: 'Discard unsaved changes?',
        description: 'Your last edits to these settings will be lost.',
        confirmLabel: 'Discard',
        danger: true,
      });
      if (!ok) return;
      applyThemeToDocument(pristine);
    }
    onClose();
  };

  const start2fa = async () => {
    setTwoErr('');
    setTwoBusy(true);
    try {
      const data = await setupTwoFactor();
      setTwoSetup(data);
      setTwoCode('');
    } catch (e) {
      setTwoErr(e.message || 'Could not start 2FA setup');
    }
    setTwoBusy(false);
  };

  const confirm2fa = async () => {
    setTwoErr('');
    setTwoBusy(true);
    try {
      await enableTwoFactor(twoCode.replace(/\D/g, ''));
      setTwoSetup(null);
      setTwoCode('');
      onAccountRefresh?.();
    } catch (e) {
      setTwoErr(e.message || 'Invalid code');
    }
    setTwoBusy(false);
  };

  const runDisable2fa = async () => {
    setTwoErr('');
    setTwoBusy(true);
    try {
      await disableTwoFactor(twoDisableCode.replace(/\D/g, ''));
      setTwoDisableCode('');
      onAccountRefresh?.();
    } catch (e) {
      setTwoErr(e.message || 'Could not disable');
    }
    setTwoBusy(false);
  };

  const upgrade = async (plan) => {
    setPlanBusy(plan);
    setTwoErr('');
    try {
      await selectBillingPlan(plan);
      onAccountRefresh?.();
    } catch (e) {
      setTwoErr(e.message || 'Upgrade failed');
    }
    setPlanBusy(null);
  };

  const tier = account?.tier || 'free';
  const rem = account?.messages_remaining ?? 0;
  const lim = account?.monthly_limit ?? 15;

  return (
    <div className="modal-overlay settings-overlay anim-fade" onClick={handleClose}>
      <div
        className="modal-content settings-modal glass-elevated anim-modal-pop"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <div className="settings-modal-head">
          <div>
            <h2 id="settings-title">Settings</h2>
            <p className="settings-user-line">
              <Sparkles size={14} /> {userEmail}
            </p>
          </div>
          <button type="button" className="settings-close-btn" onClick={handleClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="settings-tabs">
          {['general', 'appearance', 'security', 'billing', 'help', 'privacy'].map((id) => {
            const Icon = TAB_ICONS[id];
            return (
              <button
                key={id}
                type="button"
                className={`settings-tab ${tab === id ? 'active' : ''}`}
                onClick={() => setTab(id)}
              >
                <Icon size={16} />
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            );
          })}
        </div>

        {twoErr && (
          <div className="warning-card settings-global-err" style={{ marginBottom: 12 }}>
            {twoErr}
          </div>
        )}

        <div className="settings-body">
          {tab === 'general' && (
            <div className="settings-section anim-fade">
              <h3>Dismissed analyses (trash)</h3>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.trashAutocleanEnabled}
                  onChange={(e) => patchLocal({ trashAutocleanEnabled: e.target.checked })}
                />
                <span>Auto-remove trash entries after…</span>
              </label>
              <div className="setting-indent">
                <input
                  type="number"
                  min={1}
                  max={3650}
                  className="btn btn-sm num-input"
                  disabled={!local.trashAutocleanEnabled}
                  value={local.trashAutocleanDays}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    patchLocal({
                      trashAutocleanDays: Number.isFinite(v)
                        ? Math.max(1, Math.min(3650, v))
                        : local.trashAutocleanDays,
                    });
                  }}
                />
                <span className="setting-hint">days</span>
              </div>

              <h3>Analysis defaults</h3>
              <label className="setting-row">
                <span>
                  Sensitivity
                  <span className="setting-desc">High hides routine/low-importance results by default.</span>
                </span>
                <select
                  value={local.analysisSensitivity}
                  onChange={(e) => patchLocal({ analysisSensitivity: e.target.value })}
                >
                  <option value="low">Low — show everything</option>
                  <option value="medium">Medium — hide low</option>
                  <option value="high">High — focus on important+</option>
                </select>
              </label>
              <label className="setting-row">
                <span>Max emails per batch (UI cap)</span>
                <select
                  value={local.maxEmailsPerBatch}
                  onChange={(e) => patchLocal({ maxEmailsPerBatch: Number(e.target.value) })}
                >
                  {[50, 100, 200, 500].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.autoRefresh}
                  onChange={(e) => patchLocal({ autoRefresh: e.target.checked })}
                />
                <span>Auto-refresh folder lists when returning to Inbox</span>
              </label>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.soundEffects}
                  onChange={(e) => patchLocal({ soundEffects: e.target.checked })}
                />
                <span>Subtle sound when a batch completes</span>
              </label>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.compactResults}
                  onChange={(e) => patchLocal({ compactResults: e.target.checked })}
                />
                <span>Compact result cards</span>
              </label>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.showAnalysisTips}
                  onChange={(e) => patchLocal({ showAnalysisTips: e.target.checked })}
                />
                <span>Show tips on the New Analysis page</span>
              </label>
            </div>
          )}

          {tab === 'appearance' && (
            <div className="settings-section anim-fade">
              <h3>Theme</h3>
              <label className="setting-row">
                <span>Color mode</span>
                <select
                  value={local.theme}
                  onChange={(e) => patchLocal({ theme: e.target.value })}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="system">Match system</option>
                </select>
              </label>
              <label className="setting-row">
                <input
                  type="checkbox"
                  checked={local.reducedMotion}
                  onChange={(e) => patchLocal({ reducedMotion: e.target.checked })}
                />
                <span>Reduce motion (respects accessibility)</span>
              </label>
              <p className="setting-hint-block">
                Theme & motion changes preview live. Use Save to make them stick across reloads.
              </p>
            </div>
          )}

          {tab === 'security' && (
            <div className="settings-section anim-fade">
              <h3>Two-factor authentication (TOTP)</h3>
              <p className="setting-hint-block">
                Works with Google Authenticator, 1Password, Authy, etc. After enabling, you will enter a code each time
                you sign in on a new browser session.
              </p>
              {account?.two_fa_enabled ? (
                <div className="twofa-enabled-block">
                  <p className="ok-badge">2FA is enabled</p>
                  <label className="twofa-disable">
                    <span>Code to disable</span>
                    <input
                      className="search-input"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoDisableCode}
                      onChange={(e) => setTwoDisableCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                    />
                  </label>
                  <button type="button" className="btn btn-danger" disabled={twoBusy} onClick={runDisable2fa}>
                    {twoBusy ? <Loader2 className="btn-spinner" size={16} /> : null}
                    Disable 2FA
                  </button>
                </div>
              ) : !twoSetup ? (
                <button type="button" className="btn btn-primary" disabled={twoBusy} onClick={start2fa}>
                  {twoBusy ? <Loader2 className="btn-spinner" size={16} /> : null}
                  Set up authenticator
                </button>
              ) : (
                <div className="twofa-setup-block">
                  {twoSetup.qr_png_base64 ? (
                    <img
                      className="twofa-qr"
                      alt="QR code"
                      src={`data:image/png;base64,${twoSetup.qr_png_base64}`}
                    />
                  ) : (
                    <pre className="twofa-uri">{twoSetup.otpauth_url}</pre>
                  )}
                  <p className="setting-hint-block">Or enter secret manually: {twoSetup.secret}</p>
                  <input
                    className="search-input"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={twoCode}
                    onChange={(e) => setTwoCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <div className="settings-btn-row">
                    <button type="button" className="btn btn-primary" disabled={twoBusy} onClick={confirm2fa}>
                      Confirm & enable
                    </button>
                    <button type="button" className="btn" onClick={() => setTwoSetup(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <h3>Account strikes</h3>
              <p className="setting-hint-block">
                Strikes escalate if automated systems detect abusive usage (for example, hammering the API to burn
                credits): 1 day → 3 → 7 → 30 → 90 → 365 → permanent suspension.
              </p>
              <p className="strike-readout">
                Current strike level: <strong>{account?.strike_level ?? 0}</strong>
              </p>
            </div>
          )}

          {tab === 'billing' && (
            <div className="settings-section anim-fade billing-section">
              <div className="quota-hero glass">
                <div className="quota-hero-label">This month</div>
                <div className="quota-hero-num">
                  {rem} <span className="quota-slash">/</span> {lim}
                </div>
                <div className="quota-hero-sub">AI analyses remaining</div>
                <div className="tier-pill-live" data-tier={tier}>
                  {tier === 'free' && 'Free'}
                  {tier === 'pro' && 'Pro'}
                  {tier === 'elite' && 'Elite 24/7'}
                </div>
              </div>

              <div className="plan-grid">
                <div className={`plan-card glass ${tier === 'free' ? 'current' : ''}`}>
                  <h4>Free</h4>
                  <div className="plan-price">$0</div>
                  <ul>
                    <li>15 AI analyses / month</li>
                    <li>Core inbox tools</li>
                  </ul>
                  {tier === 'free' && <span className="plan-current-tag">Current</span>}
                </div>
                <div className={`plan-card glass pro ${tier === 'pro' ? 'current' : ''}`}>
                  <h4>Pro</h4>
                  <div className="plan-price">$9 / mo</div>
                  <p className="plan-note">Demo: mock checkout (no real charge)</p>
                  <ul>
                    <li>500 analyses / month</li>
                    <li>Higher rate limits</li>
                  </ul>
                  {tier !== 'pro' ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={planBusy}
                      onClick={() => upgrade('pro')}
                    >
                      {planBusy === 'pro' ? <Loader2 className="btn-spinner" size={16} /> : 'Unlock Pro'}
                    </button>
                  ) : (
                    <span className="plan-current-tag">Current</span>
                  )}
                </div>
                <div className={`plan-card glass elite ${tier === 'elite' ? 'current' : ''}`}>
                  <h4>Elite 24/7</h4>
                  <div className="plan-price">$36 / mo</div>
                  <p className="plan-note">~4× Pro — built for always-on teams</p>
                  <ul>
                    <li>5,000 analyses / month</li>
                    <li>Generous burst limits</li>
                  </ul>
                  {tier !== 'elite' ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={planBusy}
                      onClick={() => upgrade('elite')}
                    >
                      {planBusy === 'elite' ? <Loader2 className="btn-spinner" size={16} /> : 'Go Elite'}
                    </button>
                  ) : (
                    <span className="plan-current-tag">Current</span>
                  )}
                </div>
              </div>
              <p className="setting-hint-block">
                Production deployments should verify payments with Stripe (or similar) server-side before upgrading tiers.
              </p>
            </div>
          )}

          {tab === 'help' && (
            <div className="settings-section help-content anim-fade">
              <h3>How to use InboxPulse</h3>
              <ol>
                <li>Connect Gmail</li>
                <li>Pick a mode on New Analysis</li>
                <li>Review AI importance & summaries</li>
              </ol>
              <h3>Tips</h3>
              <ul>
                <li>Free tier counts unique AI analyses toward your monthly pool.</li>
                <li>Enable 2FA in Security before you store sensitive runs on this device.</li>
                <li>Use Pick messages to build a cross-run collection.</li>
              </ul>
              <p>
                <strong>Contact:</strong> support@inboxpulse.com
              </p>
            </div>
          )}

          {tab === 'privacy' && (
            <div className="settings-section privacy-content anim-fade">
              <p>
                <strong>Your data:</strong>
              </p>
              <ul>
                <li>Summaries are stored in your browser for saved runs unless you clear site data.</li>
                <li>Account tier, usage, and security settings are stored on the server for billing and protection.</li>
                <li>OAuth tokens are encrypted in cookies.</li>
              </ul>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <div className="settings-footer-left">
            <button type="button" className="btn btn-sm" onClick={handleResetDefaults}>
              <RotateCcw size={14} /> Reset defaults
            </button>
            {dirty && (
              <button type="button" className="btn btn-sm" onClick={handleDiscard}>
                Discard
              </button>
            )}
            <div className={`settings-save-status status-${saveState}`} aria-live="polite">
              {saveState === 'saved' && (
                <>
                  <CheckCircle2 size={14} /> Saved
                </>
              )}
              {saveState === 'idle' && dirty && (
                <span className="settings-dirty-dot">Unsaved changes</span>
              )}
            </div>
          </div>
          <div className="settings-footer-right">
            <button
              type="button"
              className={`btn btn-primary settings-save-btn ${dirty ? 'is-dirty' : ''}`}
              onClick={handleSave}
              disabled={!dirty || saveState === 'saving'}
            >
              {saveState === 'saving' ? <Loader2 size={16} className="btn-spinner" /> : <Save size={16} />}
              {saveState === 'saving' ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
